import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Edit,
  FolderTree,
  Hash,
  ImageIcon,
  Layers3,
  Loader2,
  Package,
  Trash2,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { categoriesApi, getApiValidationErrors } from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Categories/categories.css';
import '../../styles/Categories/category-details.css';

function CategoryDetails() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();

  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const categoryName = useMemo(() => {
    return getTranslatedField(
      category,
      'name',
      language,
      'fr',
    );
  }, [category, language]);

  const categoryDescription = useMemo(() => {
    return getTranslatedField(
      category,
      'description',
      language,
      'fr',
    );
  }, [category, language]);

  const parentCategoryName = useMemo(() => {
    return getTranslatedField(
      category?.parent,
      'name',
      language,
      'fr',
    );
  }, [category, language]);

  const children = Array.isArray(category?.children)
    ? category.children
    : [];

  const imageUrl = resolveMediaUrl(
    category?.image_url ||
      category?.image_path ||
      category?.image,
  );

  const fetchCategory = async () => {
    try {
      setLoading(true);
      setError('');
      setValidationErrors({});

      const response =
        await categoriesApi.getCategory(categoryId);

      setCategory(
        response.data?.data?.category ||
          response.data?.data ||
          null,
      );
    } catch (apiError) {
      const translatedError =
        translateApiError(apiError, t);

      setError(translatedError);

      setValidationErrors(
        getApiValidationErrors(apiError),
      );

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('categories.actions.delete'),
      message: t('categories.confirmDelete'),
      itemName:
        categoryName ||
        t('categories.noName'),
      confirmText: t(
        'categories.actions.delete',
      ),
      cancelText: t('common.cancel'),
      closeText: t('common.close'),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await categoriesApi.deleteCategory(
        categoryId,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t(
          'categories.alerts.deleted',
        ),
      });

      navigate('/categories');
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(translatedError);

      setValidationErrors(
        getApiValidationErrors(
          apiError,
        ),
      );

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    }
  };

  if (loading) {
    return (
      <main className="category-details-page">
        <section className="category-details-state-panel">
          <div className="category-details-loading">
            <Loader2 size={30} />

            <span>
              {t('categories.loading')}
            </span>
          </div>
        </section>
      </main>
    );
  }

  if (error || !category) {
    return (
      <main className="category-details-page">
        <section className="category-details-error-panel">
          <div className="category-details-error-copy">
            <FolderTree size={34} />

            <h1>
              {t(
                'categories.details.notFound',
              )}
            </h1>

            {error ? <p>{error}</p> : null}

            {validationErrors.category ? (
              <p>
                {
                  validationErrors
                    .category[0]
                }
              </p>
            ) : null}
          </div>

          <Link
            to="/categories"
            className="category-details-back-button"
          >
            <ArrowLeft
              size={18}
              className="category-details-back-icon"
            />

            <span>
              {t(
                'categories.form.backToCategories',
              )}
            </span>
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="category-details-page">
      <header className="category-details-header">
        <Link
          to="/categories"
          className="category-details-back-button"
        >
          <ArrowLeft
            size={18}
            className="category-details-back-icon"
          />

          <span>
            {t(
              'categories.form.backToCategories',
            )}
          </span>
        </Link>

        <div className="category-details-header__main">
          <div className="category-details-header__copy">
            <p className="category-details-eyebrow">
              {t('categories.eyebrow')}
            </p>

            <h1>
              {categoryName ||
                t('categories.noName')}
            </h1>

            <p className="category-details-header__description">
              {categoryDescription ||
                t(
                  'categories.noDescription',
                )}
            </p>
          </div>

          <div className="category-details__actions">
            <Link
              to={`/categories/${category.id}/edit`}
              className="category-details-primary-button"
            >
              <Edit size={18} />

              <span>
                {t(
                  'categories.actions.edit',
                )}
              </span>
            </Link>

            <button
              type="button"
              className="category-details-secondary-button category-details__delete"
              onClick={handleDelete}
            >
              <Trash2 size={18} />

              <span>
                {t(
                  'categories.actions.delete',
                )}
              </span>
            </button>
          </div>
        </div>
      </header>

      <section className="category-details-overview">
        <article className="category-details-card category-details-card--media">
          <div className="category-details-main-image">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={
                  categoryName ||
                  t('categories.noName')
                }
              />
            ) : (
              <div className="category-details-main-image__placeholder">
                <span className="category-details-main-image__placeholder-icon">
                  <FolderTree size={38} />
                </span>

                <strong>
                  {t(
                    'categories.details.noImage',
                  )}
                </strong>
              </div>
            )}

            <span
              className={`category-details-media-status ${
                category.is_active
                  ? 'is-active'
                  : 'is-inactive'
              }`}
            >
              {category.is_active
                ? t(
                    'categories.status.active',
                  )
                : t(
                    'categories.status.inactive',
                  )}
            </span>
          </div>

          <div className="category-details-media-meta">
            <div>
              <span>
                {t(
                  'categories.details.type',
                )}
              </span>

              <strong>
                {category.parent_id
                  ? t(
                      'categories.details.subcategory',
                    )
                  : t(
                      'categories.details.mainCategory',
                    )}
              </strong>
            </div>

            <div>
              <span>
                {t(
                  'categories.table.sortOrder',
                )}
              </span>

              <strong>
                {category.sort_order ?? 0}
              </strong>
            </div>
          </div>
        </article>

        <article className="category-details-card category-details-card--summary">
          <div className="category-details-summary-head">
            <div>
              <span>
                {t(
                  'categories.details.type',
                )}
              </span>

              <h2>
                {categoryName ||
                  t(
                    'categories.noName',
                  )}
              </h2>
            </div>

            <span
              className={`category-details-status ${
                category.is_active
                  ? 'category-details-status--active'
                  : 'category-details-status--inactive'
              }`}
            >
              {category.is_active
                ? t(
                    'categories.status.active',
                  )
                : t(
                    'categories.status.inactive',
                  )}
            </span>
          </div>

          <div className="category-details-metric-grid">
            <article>
              <span>
                <Package size={19} />
              </span>

              <div>
                <strong>
                  {category.products_count ??
                    0}
                </strong>

                <small>
                  {t(
                    'categories.details.productsCount',
                  )}
                </small>
              </div>
            </article>

            <article>
              <span>
                <Layers3 size={19} />
              </span>

              <div>
                <strong>
                  {children.length}
                </strong>

                <small>
                  {t(
                    'categories.details.subcategoriesCount',
                  )}
                </small>
              </div>
            </article>
          </div>

          <div className="category-details-info-list">
            {category.parent_id ? (
              <div>
                <span>
                  <FolderTree size={18} />
                </span>

                <div>
                  <small>
                    {t(
                      'categories.details.parentCategory',
                    )}
                  </small>

                  <strong>
                    {parentCategoryName ||
                      '-'}
                  </strong>
                </div>
              </div>
            ) : null}

            <div>
              <span>
                <Hash size={18} />
              </span>

              <div>
                <small>
                  {t(
                    'categories.table.sortOrder',
                  )}
                </small>

                <strong>
                  {category.sort_order ?? 0}
                </strong>
              </div>
            </div>

            <div>
              <span>
                <Layers3 size={18} />
              </span>

              <div>
                <small>
                  {t(
                    'categories.details.slug',
                  )}
                </small>

                <strong
                  dir="ltr"
                  className="category-details-ltr-value"
                >
                  {category.slug || '-'}
                </strong>
              </div>
            </div>

            <div>
              <span>
                <ImageIcon size={18} />
              </span>

              <div>
                <small>
                  {t(
                    'categories.details.image',
                  )}
                </small>

                <strong>
                  {imageUrl
                    ? t(
                        'categories.details.imageAvailable',
                      )
                    : t(
                        'categories.details.noImage',
                      )}
                </strong>
              </div>
            </div>
          </div>
        </article>
      </section>

      {!category.parent_id &&
      children.length > 0 ? (
        <section className="category-details-card category-details-section">
          <div className="category-details-section__head">
            <div>
              <span className="category-details-section__icon">
                <FolderTree size={20} />
              </span>

              <div>
                <h2>
                  {t(
                    'categories.details.subcategories',
                  )}
                </h2>

                <p>
                  {t(
                    'categories.details.subcategoriesCount',
                  )}
                </p>
              </div>
            </div>

            <strong>
              {children.length}
            </strong>
          </div>

          <div className="category-details-subcategories">
            {children.map((child) => {
              const childName =
                getTranslatedField(
                  child,
                  'name',
                  language,
                  'fr',
                );

              const childDescription =
                getTranslatedField(
                  child,
                  'description',
                  language,
                  'fr',
                );

              return (
                <Link
                  to={`/categories/${child.id}`}
                  className="category-details-subcategory"
                  key={child.id}
                >
                  <span>
                    <FolderTree
                      size={19}
                    />
                  </span>

                  <div>
                    <strong>
                      {childName ||
                        t(
                          'categories.noName',
                        )}
                    </strong>

                    <small>
                      {childDescription ||
                        t(
                          'categories.noDescription',
                        )}
                    </small>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="category-details-card category-details-section">
        <div className="category-details-section__head">
          <div>
            <span className="category-details-section__icon">
              <FolderTree size={20} />
            </span>

            <div>
              <h2>
                {t(
                  'categories.form.description',
                )}
              </h2>

              <p>
                {t(
                  'categories.details.type',
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="category-details-description">
          <p>
            {categoryDescription ||
              t(
                'categories.noDescription',
              )}
          </p>
        </div>
      </section>
    </main>
  );
}

export default CategoryDetails;